import type { SmartMarketplaceRecommendation } from '@/api/smartMarketplace';

const SENTENCE_BREAK = /(?<=[.!?])\s+(?=[A-Z])/;

function humanize(value: string): string {
  return value.replace(/_/g, ' ');
}

function pointKey(text: string): string {
  return text.replace(/\s+/g, ' ').trim().replace(/[.!?]+$/, '').toLowerCase();
}

/** Split a legacy paragraph into sentences. Decimals such as 0.414 stay in one point. */
export function prosePoints(text: unknown): string[] {
  if (typeof text !== 'string') return [];
  const stripped = text.trim();
  if (!stripped) return [];
  const chunks = stripped.includes('\n') ? stripped.split(/\n+/) : [stripped];
  const points: string[] = [];
  for (const chunk of chunks) {
    const line = chunk.trim().replace(/^[-*•]\s*/, '').trim();
    if (!line) continue;
    const parts = line
      .split(SENTENCE_BREAK)
      .map((part) => part.trim())
      .filter(Boolean);
    points.push(...(parts.length ? parts : [line]));
  }
  return points;
}

export function rationaleBullets(decision: {
  rationale_points?: unknown;
  decision_rationale?: unknown;
}): string[] {
  const points = Array.isArray(decision.rationale_points)
    ? decision.rationale_points
        .filter((item): item is string => typeof item === 'string' && item.trim() !== '')
        .map((item) => item.trim())
    : [];
  const prose = prosePoints(decision.decision_rationale);
  if (points.length === 0) return prose;
  const seen = new Set(points.map(pointKey));
  const extras = prose.filter((sentence) => !seen.has(pointKey(sentence)));
  if (pointKey(prose.join(' ')) === pointKey(points.join(' '))) return points;
  return [...points, ...extras];
}

export function sortRecommendations<T extends { rank?: number | null; recommendation_id: string }>(
  rows: T[] | undefined
): T[] {
  return [...(rows ?? [])].sort((left, right) => {
    const leftRank = left.rank ?? Number.MAX_SAFE_INTEGER;
    const rightRank = right.rank ?? Number.MAX_SAFE_INTEGER;
    if (leftRank !== rightRank) return leftRank - rightRank;
    return left.recommendation_id.localeCompare(right.recommendation_id);
  });
}

export function recommendationMeta(row: SmartMarketplaceRecommendation): string {
  const parts: string[] = [];
  const current = row.current_value?.trim();
  const proposed = row.proposed_value?.trim();
  if (current || proposed) parts.push(`${current || '—'} → ${proposed || '—'}`);
  if (row.execution_eligibility) parts.push(humanize(row.execution_eligibility));
  if (row.approval) parts.push(`approval ${humanize(row.approval)}`);
  if (row.guardrail_status) parts.push(`guardrails ${humanize(row.guardrail_status)}`);
  if (row.confidence) parts.push(`${humanize(row.confidence)} confidence`);
  if (row.risk_level) parts.push(`${humanize(row.risk_level)} risk`);
  return parts.join(' · ');
}

export function recommendationPoints(row: SmartMarketplaceRecommendation): string[] {
  const points = [...prosePoints(row.rationale), ...prosePoints(row.expected_impact)];
  for (const reason of row.guardrail_reasons ?? []) {
    const trimmed = reason.trim();
    if (trimmed) points.push(trimmed);
  }
  for (const dependency of row.dependencies ?? []) {
    const trimmed = dependency.trim();
    if (trimmed) points.push(`Confirm before a write: ${trimmed}`);
  }
  const window = row.measurement_window?.trim();
  if (window) points.push(window);
  for (const assumption of row.assumptions ?? []) {
    const trimmed = assumption.trim();
    if (trimmed) points.push(trimmed);
  }
  return points;
}
